import { useCallback } from 'react';
import { toPostUI } from '../../../mappers/posts/toPostUI';
import { IconKebab } from '../../../ui/icon/IconKebab';
import { IconButton } from '../../../ui/input/IconButton';
import { Box } from '../../../ui/layout/Box';
import { DataTestId } from '../../../ui/layout/DataTestIdWrapper';
import { ResizableGrid } from '../../../ui/layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../../ui/posts/PostCardGrid/PostCardGrid';
import { calculateGridCardHeight } from '../../../ui/posts/PostCardGrid/utils';
import { PostContextMenu } from '../../../ui/posts/PostContextMenu';
import { EventSource } from '../../../utils/analyticsService';
import { useIsMobile } from '../../../utils/getIsMobile';
import { hasAvailableProducts } from '../../../utils/hasAvailableProducts';
import { useGridImageOptimizer } from '../../../utils/mds/useGridImageAlias';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useProductsPreviewEnabled } from '../../../utils/useProductsPreviewEnabled';
import { withPostCallbacks } from '../../post/withPostCallbacks';
import type { BusinessResizableGridProps } from './types';

const EnchancedPostGridCardCompact = withPostCallbacks(PostCardGrid, EventSource.BUSINESS);

const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.BUSINESS);

export const BusinessResizableGrid: React.FC<BusinessResizableGridProps> = ({
    items,
    businessId,
    user,
    isService = false,
    onLoadNext,
    onPostKebabClick,
}) => {
    const productsPreviewEnabled = useProductsPreviewEnabled();
    const { getAlias: getGridImageAliasForCard } = useGridImageOptimizer();

    const getItemHeightCallback = useCallback(
        (index: number, width: number) => {
            const postModel = items[index];

            if (!postModel || postModel.isAd) {
                return 0;
            }

            return calculateGridCardHeight(width, toPostUI(postModel).content, postModel.postType, {
                withoutFooter: true,
                withProductsPreview: Boolean(
                    productsPreviewEnabled && hasAvailableProducts(postModel) && !isService,
                ),
            });
        },
        [items, productsPreviewEnabled, isService],
    );

    const renderItem = useCallback(
        (
            index: number,
            columns: number,
            isVisible?: boolean,
            cardSize?: { width: number; height: number },
        ) => {
            const postModel = items[index];

            if (!postModel || postModel.isAd) {
                return null;
            }

            const imageAlias = getGridImageAliasForCard(cardSize?.width);

            return (
                <EnchancedPostGridCardCompact
                    imageAlias={imageAlias}
                    post={postModel}
                    index={index}
                    gridColumns={columns}
                    isVisible={isVisible}
                    showFooter={false}
                    additionalIcon={
                        <EnhancedPostContextMenu
                            index={index}
                            trigger={
                                <IconButton
                                    backgroundColor="rgba(0, 0, 0, 0.24)"
                                    color="var(--control-secondary-blur-color)"
                                    shape="rect"
                                    size="xs"
                                    dataTestId={DataTestId.POST_CARD_KEBAB_BUTTON}
                                >
                                    <IconKebab
                                        fontSize={16}
                                        dataTestId={DataTestId.POST_CARD_KEBAB}
                                    />
                                </IconButton>
                            }
                            onTriggerClick={onPostKebabClick}
                            post={postModel}
                            user={user}
                            fromBusinessId={businessId}
                        />
                    }
                    hideReportedPosts={false}
                    isCompact
                    isShowStatus
                    withProductsPreview={Boolean(productsPreviewEnabled && !isService)}
                    hasLogPostViewEvent
                />
            );
        },
        [
            items,
            onPostKebabClick,
            user,
            businessId,
            productsPreviewEnabled,
            isService,
            getGridImageAliasForCard,
        ],
    );

    const isMobile = useIsMobile();
    const isWideProfileExp = Number(useExperimentFlag('rythm_disable_wide_profile')) !== 1;
    const isWideProfile = isWideProfileExp && !isMobile;

    if (!items.length) {
        return null;
    }

    return (
        <Box
            p={isWideProfile ? 0 : 2}
            pr={isWideProfile ? 1 : 2}
            background={isWideProfile ? 'none' : 'var(--background-primary)'}
        >
            <ResizableGrid
                minItemWidth={210}
                items={items}
                loadMore={onLoadNext}
                getItemHeight={getItemHeightCallback}
                renderItem={renderItem}
            />
        </Box>
    );
};
